import {
  DEFAULT_FORM,
  DEFAULT_LAYOUT,
  DEFAULT_PALETTE,
  paletteMode,
} from "../appearance/registry";
import { SCHEMA_VERSION, type Locale, type SessionState } from "./schema";

/**
 * Country -> home currency for first-time visitors. Fed by the Cloudflare
 * `cf.country` header once the Worker passes it through; until then callers
 * pass undefined and get the USD default.
 */
const COUNTRY_CURRENCY: Readonly<Record<string, string>> = {
  SA: "SAR",
  EG: "EGP",
  AE: "AED",
};

export const FALLBACK_BASE = "USD";

/**
 * The seeded list for a first-time visitor. Never an empty state.
 *
 * SIX, AND CHOSEN FOR THIS AUDIENCE. Two rows on a tall phone read as a broken
 * screen rather than a minimal one, and no layout trick fixes that as honestly
 * as having something to show. The six are weighted to Gulf and Arab users
 * rather than a generic global set:
 *
 *   USD  the reference every regional currency is pegged or compared against
 *   AED  the most common intra-Gulf travel and trade currency
 *   EGP  the largest Arab expatriate corridor in the Gulf
 *   EUR  travel and imports
 *   GBP  travel and education
 *
 * The base currency is prepended and de-duplicated, so a Saudi visitor gets
 * SAR first and an Emirati does not see AED twice.
 */
export const SEED_QUOTES: readonly string[] = ["USD", "AED", "EGP", "EUR", "GBP"];

/** Total rows a fresh install opens with, base included. */
export const SEED_COUNT = 6;

export function localeFromNavigator(language: string | undefined): Locale {
  return typeof language === "string" && language.toLowerCase().startsWith("ar")
    ? "ar"
    : "en";
}

export function baseFromCountry(country: string | undefined): string {
  if (typeof country !== "string") return FALLBACK_BASE;
  return COUNTRY_CURRENCY[country.toUpperCase()] ?? FALLBACK_BASE;
}

export function defaultAppearance() {
  return {
    palette: DEFAULT_PALETTE,
    form: DEFAULT_FORM,
    layout: DEFAULT_LAYOUT,
    colorScheme: paletteMode(DEFAULT_PALETTE),
  } as const;
}

export function defaultSession(options?: {
  country?: string | undefined;
  language?: string | undefined;
}): SessionState {
  const base = baseFromCountry(options?.country);
  // Home currency first, then the seeded set, de-duplicated and capped.
  const quotes = [base, ...SEED_QUOTES]
    .filter((code, index, all) => all.indexOf(code) === index)
    .slice(0, SEED_COUNT);

  return {
    v: SCHEMA_VERSION,
    base,
    amount: "1",
    quotes,
    locale: localeFromNavigator(options?.language),
    appearance: defaultAppearance(),
  };
}
